import {
  useAiUsageCalls,
  useAiUsageLabel,
  useAiUsageSummary,
} from "@/lib/hooks/aiUsage/useAiUsage";
import {
  DEFAULT_TWD_PER_USD,
  formatCostPerSlide,
  formatTokens,
  formatTwd,
} from "@/lib/utils/aiCost";
import { Button, LoadingSpinner } from "@ai-teach/ui-web";
import { Fragment, useMemo, useState } from "react";
import { HiOutlineX } from "react-icons/hi";

export const AI_USAGE_GROUPS = [
  "lesson",
  "book",
  "editor",
  "template",
  "day",
  "batch",
  "job",
  "outcome",
] as const;

export type AiUsageGroupBy = (typeof AI_USAGE_GROUPS)[number];

const GROUP_LABELS: Record<AiUsageGroupBy, string> = {
  lesson: "Lesson",
  book: "Book lesson",
  editor: "Editor",
  template: "Template",
  day: "Day",
  batch: "Batch",
  job: "Draft run",
  outcome: "Outcome",
};

export const RANGES = [7, 30, 90, 0] as const;

const rangeLabel = (days: number) => (days === 0 ? "All time" : `${days} days`);

export interface AiUsageFilters {
  groupBy: AiUsageGroupBy;
  days: number;
  /** NT$ per US$, since providers bill in USD. */
  rate: number;
  lesson?: string | undefined;
  editor?: string | undefined;
  template?: string | undefined;
  batch?: string | undefined;
  job?: string | undefined;
}

interface UsageTotals {
  costUsd: number;
  wastedCostUsd: number;
  calls: number;
  slidesUsable: number;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  lessons: number;
}

function Totals({ totals, rate }: { totals: UsageTotals; rate: number }) {
  const {
    costUsd,
    wastedCostUsd,
    calls,
    slidesUsable,
    inputTokens,
    cachedInputTokens,
    outputTokens,
    lessons,
  } = totals;

  const cells = [
    { label: "LLM spend", value: formatTwd(costUsd, rate) },
    { label: "Per slide", value: formatCostPerSlide(costUsd, slidesUsable, rate) },
    { label: "Nothing kept", value: formatTwd(wastedCostUsd, rate) },
    { label: "Model calls", value: String(calls) },
    { label: "Slides drafted", value: String(slidesUsable) },
    { label: "Lessons", value: String(lessons) },
    {
      label: "Tokens in · cached · out",
      value: `${formatTokens(inputTokens)} · ${formatTokens(cachedInputTokens)} · ${formatTokens(outputTokens)}`,
    },
  ];

  return (
    <dl className="mb-6 grid grid-cols-2 gap-4 rounded-xl border border-gray-200 px-5 py-4 sm:grid-cols-4 lg:grid-cols-7 dark:border-gray-800">
      {cells.map((cell) => (
        <div key={cell.label}>
          <dt className="text-[11px] uppercase tracking-wider text-gray-400">{cell.label}</dt>
          <dd className="mt-0.5 font-mono text-sm text-gray-900 dark:text-gray-100">
            {cell.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function CallList({ jobId, rate }: { jobId: string; rate: number }) {
  const { calls, loading } = useAiUsageCalls(jobId);

  if (loading && calls.length === 0) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }
  if (calls.length === 0) {
    return (
      <p className="py-6 text-center text-xs text-gray-500">
        No model calls recorded for this run.
      </p>
    );
  }

  return (
    <table className="w-full text-xs">
      <thead className="text-left text-[11px] uppercase tracking-wider text-gray-400">
        <tr>
          <th className="py-2 pr-3 font-medium">Template</th>
          <th className="py-2 pr-3 font-medium">Section</th>
          <th className="py-2 pr-3 font-medium">Kind</th>
          <th className="py-2 pr-3 font-medium">Outcome</th>
          <th className="py-2 pr-3 text-right font-medium">Slides</th>
          <th className="py-2 pr-3 text-right font-medium">Prompt</th>
          <th className="py-2 pr-3 text-right font-medium">Tokens</th>
          <th className="py-2 text-right font-medium">Cost</th>
        </tr>
      </thead>
      <tbody>
        {calls.map((call) => (
          <tr key={call.id} className="border-t border-gray-200 dark:border-gray-800">
            <td className="py-1.5 pr-3">{call.template}</td>
            <td className="py-1.5 pr-3 font-mono text-[11px] text-gray-400">
              {call.sectionId.slice(0, 8)}
            </td>
            <td className="py-1.5 pr-3 text-gray-500">
              {[call.isRedraft ? "redraft" : "draft", call.isRetry ? "retry" : ""]
                .filter((part) => part !== "")
                .join(" · ")}
            </td>
            <td className="py-1.5 pr-3 text-gray-500">{call.outcome}</td>
            <td className="py-1.5 pr-3 text-right font-mono">
              {call.slidesUsable}/{call.slidesRequested}
            </td>
            <td className="py-1.5 pr-3 text-right font-mono text-gray-500">
              {formatTokens(call.promptChars)}
            </td>
            <td className="py-1.5 pr-3 text-right font-mono text-gray-500">
              {formatTokens(call.inputTokens)} in · {formatTokens(call.outputTokens)} out
            </td>
            <td className="py-1.5 text-right font-mono">{formatTwd(call.costUsd, rate)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AiUsagePage({
  filters,
  onFiltersChange,
}: {
  filters: AiUsageFilters;
  onFiltersChange: (next: Partial<AiUsageFilters>) => void;
}) {
  // Held still between renders: a range start that moved every render would change the query's
  // variables and refetch forever.
  const from = useMemo(
    () =>
      filters.days === 0 ? undefined : new Date(Date.now() - filters.days * 24 * 60 * 60 * 1000),
    [filters.days]
  );

  const { summary, loading } = useAiUsageSummary({
    groupBy: filters.groupBy,
    from,
    lessonId: filters.lesson,
    userId: filters.editor,
    template: filters.template,
    batchId: filters.batch,
    limit: 100,
  });

  const lessonLabel = useAiUsageLabel("lesson", filters.lesson);
  const editorLabel = useAiUsageLabel("editor", filters.editor);
  const batchLabel = useAiUsageLabel("batch", filters.batch);
  const [search, setSearch] = useState("");

  const active = [
    { key: "lesson" as const, name: "Lesson", label: lessonLabel, value: filters.lesson },
    { key: "editor" as const, name: "Editor", label: editorLabel, value: filters.editor },
    {
      key: "template" as const,
      name: "Template",
      label: filters.template,
      value: filters.template,
    },
    { key: "batch" as const, name: "Batch", label: batchLabel, value: filters.batch },
  ].filter((one) => one.value);

  const rows = (summary?.rows ?? []).filter((row) =>
    row.label.toLowerCase().includes(search.trim().toLowerCase())
  );

  // Drilling into a row narrows the same table rather than opening another page: a lesson row
  // becomes that lesson split by template, an editor row that editor's lessons.
  const drillInto = (key: string) => {
    if (filters.groupBy === "job") {
      onFiltersChange({ job: filters.job === key ? undefined : key });
      return;
    }
    if (filters.groupBy === "lesson") onFiltersChange({ lesson: key, groupBy: "template" });
    if (filters.groupBy === "editor") onFiltersChange({ editor: key, groupBy: "lesson" });
    if (filters.groupBy === "template") onFiltersChange({ template: key, groupBy: "lesson" });
    if (filters.groupBy === "batch") onFiltersChange({ batch: key, groupBy: "lesson" });
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="mt-1 text-2xl font-bold">AI Cost</h1>
      <p className="mb-5 mt-1 text-xs text-gray-500 dark:text-gray-400">
        LLM spend on drafting slides, one row per model call and kept forever. Speech and image
        generation are not counted here.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {RANGES.map((days) => (
          <Button
            key={days}
            size="sm"
            variant={filters.days === days ? "primary" : "ghost"}
            onClick={() => onFiltersChange({ days })}
          >
            {rangeLabel(days)}
          </Button>
        ))}

        <label className="ml-auto flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          NT$ per US$
          <input
            type="number"
            min={1}
            step={0.5}
            value={filters.rate}
            onChange={(event) =>
              onFiltersChange({ rate: Number(event.target.value) || DEFAULT_TWD_PER_USD })
            }
            className="w-20 rounded-lg border border-gray-300 bg-transparent px-2 py-1 text-right font-mono text-xs dark:border-gray-700"
          />
        </label>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-gray-200 pb-4 dark:border-gray-800">
        {AI_USAGE_GROUPS.map((group) => (
          <button
            key={group}
            type="button"
            onClick={() => onFiltersChange({ groupBy: group, job: undefined })}
            className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${
              filters.groupBy === group
                ? "bg-primary-500/10 text-primary-600 dark:text-primary-300"
                : "text-gray-500 hover:bg-black/5 dark:hover:bg-white/5"
            }`}
          >
            {GROUP_LABELS[group]}
          </button>
        ))}

        <label className="ml-auto flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={`Find a ${GROUP_LABELS[filters.groupBy].toLowerCase()}…`}
            className="w-48 rounded-lg border border-gray-300 bg-transparent px-2 py-1 text-xs dark:border-gray-700"
          />
        </label>
      </div>

      {active.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {active.map((one) => (
            <button
              key={one.key}
              type="button"
              onClick={() => onFiltersChange({ [one.key]: undefined })}
              className="flex max-w-lg items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] text-gray-600 dark:bg-white/5 dark:text-gray-300"
            >
              <span className="text-gray-400">{one.name}</span>
              <span className="truncate font-medium">{one.label}</span>
              <HiOutlineX className="h-3 w-3 shrink-0" />
            </button>
          ))}
        </div>
      )}

      {loading && !summary ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : !summary || summary.total.calls === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 px-4 py-12 text-center dark:border-gray-700">
          <p className="body-sm text-gray-500 dark:text-gray-400">
            Nothing recorded for this range yet.
          </p>
        </div>
      ) : (
        <>
          <Totals totals={summary.total} rate={filters.rate} />

          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-gray-400">
              <tr>
                <th className="py-2 pr-3 font-medium">{GROUP_LABELS[filters.groupBy]}</th>
                <th className="py-2 pr-3 text-right font-medium">Calls</th>
                <th className="py-2 pr-3 text-right font-medium">Slides</th>
                <th className="py-2 pr-3 text-right font-medium">Per slide</th>
                <th className="py-2 pr-3 text-right font-medium">Nothing kept</th>
                <th className="py-2 pr-3 text-right font-medium">Tokens</th>
                <th className="py-2 text-right font-medium">Cost</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <Fragment key={row.key}>
                  <tr
                    onClick={() => drillInto(row.key)}
                    className="cursor-pointer border-t border-gray-200 hover:bg-black/5 dark:border-gray-800 dark:hover:bg-white/5"
                  >
                    <td className="max-w-md truncate py-2 pr-3">{row.label}</td>
                    <td className="py-2 pr-3 text-right font-mono text-xs text-gray-500">
                      {row.calls}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono text-xs text-gray-500">
                      {row.slidesUsable}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono text-xs text-gray-500">
                      {formatCostPerSlide(row.costUsd, row.slidesUsable, filters.rate)}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono text-xs text-gray-500">
                      {formatTwd(row.wastedCostUsd, filters.rate)}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono text-xs text-gray-500">
                      {formatTokens(row.inputTokens + row.outputTokens)}
                    </td>
                    <td className="py-2 text-right font-mono">
                      {formatTwd(row.costUsd, filters.rate)}
                    </td>
                  </tr>
                  {filters.groupBy === "job" && filters.job === row.key && (
                    <tr className="bg-black/[0.02] dark:bg-white/[0.02]">
                      <td colSpan={7} className="px-3 pb-3">
                        <CallList jobId={row.key} rate={filters.rate} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>

          {rows.length === 0 && (
            <p className="py-8 text-center text-xs text-gray-500">
              Nothing here matches “{search}”.
            </p>
          )}
        </>
      )}
    </div>
  );
}
